import React, { useEffect, useReducer, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DataGrid, GridColDef, GridColumnHeaderParams, useGridApiRef } from "@mui/x-data-grid";
import { Alert, Button, CircularProgress, Skeleton, Snackbar, SnackbarOrigin } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {Helmet} from "react-helmet";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { tokenState } from "../../../atoms/token";
import APIService from "../../../services/api.service";
import { CacheService } from "../../../common/services/cache.service";
import { ViewDownloadsDialog } from "../../dialogs/View-Downloads-Dialog";
import SearchBar from "../../app/SearchBar/SearchBar";
import SearchFilter from "../../app/SearchFilter/SearchFilter";
import environment from "../../../environment/environment";
import APIOptions from "../../../models/apiOptions";
import { Record, Records } from "../../../models/asset";
import { Cart } from "../../../models/assetResponse";
import { cartState } from "../../../atoms/cart";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { from, of, concatMap, delay, concat } from "rxjs";
import axios from "axios";
import "./SearchResult.scss";

const SearchResult = () => {
  // const itemsForDownload = useRecoilValue(itemsForDownloadState);
  // const setItemsForDownload = useSetRecoilState(itemsForDownloadState);
  const [showDialog, setShowDialog] = useState(false);
  const [masterToggle, setMasterToggle] = useState(false);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(0);
  const [reload, setReload] = useState(false);
  const [rows, setRows] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [cartItems, SetCartState] = useRecoilState(cartState);
  const [form, setForm] = useReducer(
    (form, updates) => ({...form, ...updates}),
    {}
  );
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const [filterModel, setFilterModel] = React.useState({ items: [] });
  const [sortModel, setSortModel] = React.useState([]);
  const [token, setToken] = useRecoilState(tokenState);

  const [snackState, setSnackState] = React.useState<boolean>(false);

  const { state } = useLocation();
  const navigate = useNavigate();
  const location = useLocation();
  const [filterOptions, setFilterOptions] = React.useState(Object.keys(state?.filterOptions).length > 0 ? state.filterOptions : null);
  const [filterText, setFilterText] = useState(state?.filterText ? state.filterText : '');
  const apiRef = useGridApiRef();
  const [isApiReady, setIsApiReady] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // 1. Load cart on page mount
  useEffect(() => {
    getCart();
  }, []);
  
  // 2. When state from useLocation() changes (like from SearchBar), set local states
  useEffect(() => {
    if (!state) return;
    if (state.filterOptions) setFilterOptions(state.filterOptions);
    if (state.filterText) setFilterText(state.filterText);
    if (state.paginationModel) setPaginationModel(state.paginationModel);
    if (state.sortModel) setSortModel(state.sortModel);
    if (state.filterModel) setFilterModel(state.filterModel);
    setInitialized(true);
  }, [state]);

  // 3. Search after our local options have been set
  useEffect(() => {
    if (!initialized) return;
    search(filterText, filterOptions);
  }, [initialized]);

  // 4. Re-search when pagination, filter, or sort changes
  useEffect(() => {
    if (!initialized) return;
    search(filterText, filterOptions);
  }, [paginationModel, filterModel, sortModel]);

  // 5. Setup scrolling + reselect if coming back from details page
  useEffect(() => {
    if (!apiRef.current || !state?.coords || !state?.row) return;

    setTimeout(() => {
      window.scrollTo(state.coords.x, state.coords.y);
      apiRef.current.selectRow(state.row.id, true, true);
    }, 1500); // shorter timeout, faster UX
  }, [apiRef.current, state]);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  function exportToExcelt() {
    setExcelLoading(true);
  
    let allRows: any[] = [];
    let apiCalls: any[] = [];
  
    let queryString = buildQueryString(null, null);
  
    const firstPageUrl = `${environment.eticApiUrl}search${queryString}&page=${paginationModel.page + 1}&pageSize=500`;
    const apiService = new APIService<any>(axios, new CacheService, firstPageUrl, token);
  
    apiService.getAnyAsync().then((firstPage: any) => {
      const pageCount = firstPage?.PageCount ?? 0;
      console.log('Total pages:', pageCount);
  
      // Prepare all API calls
      for (let i = 1; i <= pageCount; i++) {
        const url = `${environment.eticApiUrl}search${queryString}&page=${i}&pageSize=500`;
        const pageService = new APIService<any>(axios, new CacheService, url, token);
        apiCalls.push(from(pageService.getAnyAsync()).pipe(delay(1000))); // slight delay between requests
      }
  
      concat(...apiCalls).subscribe({
        next: (response: any) => {
          const mappedResults = (response?.Results ?? []).map((result: any) => ({
            TIC: formatTicNum(result.Asset),
            Title: result.Asset?.title,
            ParkName: result.Asset?.ticParkName,
            Type: result.Asset?.ticInfoType,
            Security: result.Asset?.ticDocType,
            Date: formatDate(result.Asset?.ticPublishDate) !== 'Invalid Date' ? formatDate(result.Asset?.ticPublishDate) : "No Date",
          }));
          allRows.push(...mappedResults);
        },
        error: (err) => {
          console.error('Error during export:', err);
          setExcelLoading(false);
        },
        complete: () => {
          const worksheet = XLSX.utils.json_to_sheet(allRows);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
          XLSX.writeFile(workbook, 'search-results.xlsx');
          setExcelLoading(false);
          console.log('Export complete');
        }
      });
    }).catch(err => {
      console.error('Initial export fetch failed:', err);
      setExcelLoading(false);
    });
  }
  
  // --- Helper to build the query string ---
  function buildQueryString(passedText, passedOptions: any = {}) {
    const localText = passedText != null ? passedText : filterText;
    const localOptions = passedOptions != null ? passedOptions : filterOptions;

    let queryParts = [`allfields=${encodeURIComponent(localText)}`];
  
    if (localOptions?.ticFromYear) queryParts.push(`ticFromYear=${localOptions.ticFromYear}`);
    if (localOptions?.ticToYear) queryParts.push(`ticToYear=${localOptions.ticToYear}`);
  
    (localOptions?.securityTypeList ?? []).forEach(doc => queryParts.push(`ticDocType=${encodeURIComponent(doc[1])}`));
    (localOptions?.parkAlphaList ?? []).forEach(park => queryParts.push(`ticParkAlpha=${encodeURIComponent(park.alpha)}`));
    (localOptions?.infoTypeList ?? []).forEach(info => queryParts.push(`ticInfoType=${encodeURIComponent(info)}`));
    (localOptions?.locationList ?? []).forEach(loc => queryParts.push(`ticLocation=${encodeURIComponent(loc)}`));
  
    if (sortModel.length && Object.keys(sortModel[0]).length > 0 && sortModel[0]?.field && sortModel[0].field !== 'action') {
      queryParts.push(`sortField=${sortModel[0].field}`);
      queryParts.push(`sortDirection=${sortModel[0].sort === 'desc' ? 'desc' : 'asc'}`);
    }
  
    return '?' + queryParts.join('&');
  }  

  const getCart = () => { 
    const url = `${environment.eticApiUrl}GetCart`;
    const apiService = new APIService<Cart>(axios, new CacheService, url, token);

    apiService.getAnyAsync().then((res:any[]) => {
      SetCartState(res);
    });
  }

  const search = (filterText, options: any = {}) => {
    setLoading(true);

    const queryString = buildQueryString(filterText, options); // uses the current filterOptions and sortModel
    const fullUrl = `${environment.eticApiUrl}search${queryString}&page=${paginationModel.page + 1}&pageSize=${paginationModel.pageSize}`;

    const apiOptions: APIOptions = new APIOptions(fullUrl, Records);
    const T = apiOptions.T;
    console.log('husky search queryString', queryString, form);
    const apiService = new APIService<typeof T>(axios, new CacheService, apiOptions.url, token);
    apiService.getAnyAsync().then((res: any) => {  
      setResults(res.ResultCount);
      let mappedRows:Record[] = [];
      let results = res.Results ?? [];

      results.map((result, index) => { 
        let asset = result.Asset;
        mappedRows.push({
          ticParentDocId: asset.ticParentDocId,
          ticParkAlpha: asset.ticParkAlpha,
          ticParkId: asset.ticParkId,
          ticParkName: asset.ticParkName,
          ticDocType: asset.ticDocType,
          ticDocTypeId: asset.ticDocTypeId,
          ticCorpAuthor: asset.ticCorpAuthor,
          ticInfoType: asset.ticInfoType,
          ticPublishDate: asset.ticPublishDate,
          ticNumber: asset.ticNumber,
          AssetCount: asset.AssetCount,
          AssetID: asset.AssetID,
          Title: asset?.title || asset?.Title,
          ticRevisionCode: asset.ticRevisionCode,
          ticDupeCode: asset.ticDupeCode,
          ticEticAdded: false,
          ticFrcAccessionNumber: "",
          ticFrcBox: "",
          ticInfoDate: "",
          ticLitigationHold: false,
          ticLocation: "",
          ticMedia: "",
          ticMicrofilmMissing: 0,
          ticModifiedDate: undefined,
          ticOriginalMissing: 0,
          ticOriginSystem: "",
          ticParentName: "",
          ticPdfMergeDate: undefined,
          ticRetentionCategory: "",
          ticRetentionDisposition: "",
          ticSecurityVerification: false,
          ticSize: "",
          ticStorage: "",
          ticSubject: "",
          IsLocked: false,
          AltText: "",
          AssetStatus: "",
          Copyright: "",
          Editable: false,
          LastEditDateTime: undefined,
          Publisher: "",
          Rating: 0,
          ResourceType: "",
          Subject: "",
          SubmittedDateTime: undefined,
          Submitter: ""
        });
      });

      setLoading(false);
      setRows(mappedRows);
      console.log('mappedRows', mappedRows);  
    });
  }

  const handleSearchClick = () => {
    //navigate(location.pathname, {state: {filterText: filterText, filterOptions: filterOptions}})
    search(filterText, filterOptions);
    setReload(true);
  }

  const handleRowClick = (rowArgs, event) => {
    console.log('husky scroll', rowArgs,event)
    let coords = {
      x: window.scrollX,
      y: window.scrollY
    };

    navigate('/record/' + rowArgs.row.ticParentDocId, 
    {
      state: {
        row: rowArgs.row,
        text: filterText,
        paginationModel,
        filterModel,
        sortModel,
        filterOptions: filterOptions, 
        coords
      }
    });
  }

  const handleSaveClick = (items) => {
    let cart = getParentDocIds(items);

    const url = `${environment.eticApiUrl}UpdateCart`;
    const apiService = new APIService<any[]>(axios, new CacheService, url, token);

    apiService.add(cart, url).then((res:any[]) => {
      getCart();
    });

    setShowDialog(false);
  }

  const handleCloseClick = () => {
    setShowDialog(false);
  }

  const handleSnackClose = () => {
    setSnackState(false);
  };

  const handleDownloadItemsClick = (items) => {
    const url = `${environment.eticApiUrl}CreateOrderFromCart`;
    const apiService = new APIService<Cart>(axios, new CacheService, url, token);
    setSnackState(true);

    apiService.getAnyAsync().then((res:any) => {
      console.log('cart  created', res);
      getCart();

      const orderUrl = `${environment.eticApiUrl}CreateOrderZip/${res.id}`;
      const orderApiService = new APIService<Cart>(axios, new CacheService, orderUrl, token);
  
      orderApiService.getAnyAsync().then((res:any) => {
        console.log('zip  creating', res);
        navigate('/orderHistory');
      });
    });
   
    setShowDialog(false);
  }

  const handleViewDownloadsClick = () => {
    setShowDialog(true);
  }

  const getNumRowsToRemove = () => {
    const paginatedRows: any[] = paginatedResult(rows, paginationModel.page, paginationModel.pageSize) ?? [];
    let count = 0;
    cartItems.forEach(row => {
      rows.forEach(item => {
        if(item.ticParentDocId === row.parentDocId)
          count += 1;
      })
    })
    return count;
  }

  const getNumRowsToAdd = () => {
    const paginatedRows: any[] = paginatedResult(rows, paginationModel.page, paginationModel.pageSize) ?? [];
    let count = rows.length;

    rows.forEach(row => {
      cartItems.forEach(item => {
        if(item.parentDocId === row.ticParentDocId)
          count -= 1;
      })
    })
    return count;
  }

  const handleRemoveAll = () => {
    const url = `${environment.eticApiUrl}UpdateCart`;
    const apiService = new APIService<any[]>(axios, new CacheService, url, token);

    apiService.add([], url).then((res:any[]) => {
      console.log('set cart', res);
      getCart();
    });
  }

  const handleAddAll = () => {
    const paginatedRows: any[] = paginatedResult(rows, paginationModel.page, paginationModel.pageSize) ?? [];

    let cart = getParentDocIds(cartItems);
    rows.forEach(row => { 
      cart.push(row.ticParentDocId);
    });

    let uniques = cart.filter((v, i, a) => a.indexOf(v) === i);

    const url = `${environment.eticApiUrl}UpdateCart`;
    const apiService = new APIService<any[]>(axios, new CacheService, url, token);

    apiService.add(uniques, url).then((res:any[]) => {
      console.log('set cart', res);
      getCart();
    });
  }

  const handleAddClick = (event, rowParams) => {
    let cart = getParentDocIds(cartItems);
    cart.push(rowParams.row.ticParentDocId);

    // get unique numbers from array
    let uniques = cart.filter((v, i, a) => a.indexOf(v) === i);

    const url = `${environment.eticApiUrl}UpdateCart`;
    const apiService = new APIService<any[]>(axios, new CacheService, url, token);

    apiService.add(uniques, url).then((res:any[]) => {
      console.log('set cart', res);
      getCart();
    });
  }

  const handleRemoveClick = (rowParams) => {
    const filteredCart = cartItems.filter((row) => row.parentDocId !== rowParams.row.ticParentDocId);
    let cart = getParentDocIds(filteredCart);
    const url = `${environment.eticApiUrl}UpdateCart`;
    const apiService = new APIService<any[]>(axios, new CacheService, url, token);

    apiService.add(cart, url).then((res:any[]) => {
      console.log('set cart', res);
      getCart();
    });
  }

  const handleToggleAll = () => {
    if(masterToggle === false) {
      handleAddAll();
      setMasterToggle(true);
    }
    else {
      handleRemoveAll();
      setMasterToggle(false);
    }
  }

  const handleRowBtnClick = (event, rowParams) => {
    if(isBtnDisabled(rowParams.row))
      handleAddClick(null, rowParams);
    else
      handleRemoveClick(rowParams);
  }

  const handleKeyUp = (event) => {
    setFilterText(event.value);
    search(event.value);
    //navigate(location.pathname, {state: {filterText: event.value, filterOptions: form}})
    setReload(true);
  }

  const getParentDocIds = (items) => {
    let parentDocIds = [];
    items.forEach(item => {
      parentDocIds.push(item.parentDocId);
    });

    return parentDocIds;
  }

  const isBtnDisabled = (row) => {
    let isDisabled = true;
    cartItems.forEach(x => {
      if(x.parentDocId == row.ticParentDocId) {
        isDisabled = false;
      }   
    });

    return isDisabled;
  }

  const formatDate = (date) => { 
    let d = new Date(date);
    return d.toLocaleDateString() == 'Invalid Date' ? 'No Date' : d.toLocaleDateString();
  }

  const formatTicNum = (row) => { 
    let formattedTicNum = row.ticNumber;
    if(row.ticRevisionCode)
      formattedTicNum += '-' + row.ticRevisionCode;
    if(row.ticDupeCode)
      formattedTicNum += '-' + row.ticDupeCode;

    return formattedTicNum;
  }

  const clearFilters = () => { 
    setInitialized(false);
    navigate('/searchResults', {state: {filterText: '', filterOptions: {}}})
  }

  const handleFilterChange = (newOptions) => {
    const updatedOptions = { ...filterOptions, ...newOptions };
    setFilterOptions(updatedOptions);
  
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  
    debounceTimerRef.current = setTimeout(() => {
      search(filterText, updatedOptions);
    }, 500); // (optional: tighten to 400ms debounce)
  };

  const paginatedResult = (array, page, pageSize) => {
    return array && array.slice(page * pageSize, page * pageSize + pageSize);
  }

  const columns: GridColDef[] = [
    ...(!environment.publicMode ? [{ field: 'action', 
      hideSortIcons: true,
      width: 150,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Button 
          id="masterToggle"
          variant={masterToggle ? "outlined" : "contained" } 
          color={masterToggle ? "primary" : "primary"}
          className=""         
          startIcon={masterToggle ? <FontAwesomeIcon icon={["fas", "folder-minus"]}/> : <FontAwesomeIcon icon={["fas", "folder-plus"]}/>}
          onClick={handleToggleAll}>
             {masterToggle ? 'Remove All (' + getNumRowsToRemove() + ')' : 'Add All (' + getNumRowsToAdd() + ')' }
            
        </Button>
      ),
      renderCell: (cellValues) => {
        return (
          <Button
            variant={isBtnDisabled(cellValues.row) ? "contained" : "outlined" } 
            color={isBtnDisabled(cellValues.row) ? "primary" : "primary"}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleRowBtnClick(event, cellValues);
            }}
            startIcon={isBtnDisabled(cellValues.row) ? <FontAwesomeIcon icon={["fas", "folder-plus"]}/> : <FontAwesomeIcon icon={["fas", "folder-minus"]}/>}
          >
            {isBtnDisabled(cellValues.row) ? 'Add' : 'Remove'}
          </Button>
        );
      }
    }] : []),
    { field: 'image', headerName: 'Image', width: 150, headerClassName: 'bold', renderCell: (cellValues) => {
      return (
        <>
          <img 
            style={{'backgroundSize': 'contain', 'width':  '100px', 'height': '100px','objectFit': 'contain' }} 
            src={`https://npgallery.nps.gov/api/ETIC/getparentthumbnail/${cellValues.row.ticParentDocId}`} 
          />
        </>    
      );
    } },
    { field: 'ticNumber', headerName: 'TIC#', width: 100, headerClassName: 'bold' },
    { field: 'Title', headerName: 'Title', width: 500, headerClassName: 'bold' },
    { field: 'ticParkName', headerName: 'Park Name', width: 300, headerClassName: 'bold' },
    { field: 'ticInfoType', headerName: 'Type', width: 125, headerClassName: 'bold' },
    { field: 'ticDocType', headerName: 'Security', width: 100, headerClassName: 'bold' },
    { field: 'ticPublishDate', headerName: 'Date', width: 100, headerClassName: 'bold' }
  ];
  
  return (
    <>
      {!environment.publicMode && showDialog &&
          <ViewDownloadsDialog
            title={"Selected Items For Download"}
            //downloads={itemsForDownload}
            closeButtonText={'Cancel'}
            handleActionButtonText={'Create Order'}
            //handleRemoveItem={handleRemoveClick} 
            //handleSaveClick={handleSaveClick}
            handleDownloadItemsClick={handleDownloadItemsClick}
            handleCloseClick={handleCloseClick}
          />
      }

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackState}
        onClose={handleSnackClose}
        //message="Order is being processed. Please check your orders for status."
        autoHideDuration={5000}>
        <Alert
          onClose={handleSnackClose}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}>
          Order is being processed. Please check your orders for status.
        </Alert>
      </Snackbar>

      <Helmet>
        <meta charSet="utf-8"/>
        <title>Search Results</title>
      </Helmet>

      {!environment.publicMode && (
      <div className="center">
        <Button 
          id="upload"
          variant="outlined" 
          className=""         
          startIcon={<FontAwesomeIcon icon={["fas", "folder-arrow-down"]} />}
          onClick={handleViewDownloadsClick}>
            Items Selected For Download ({cartItems.length})
        </Button>
      </div>
      )}

      <div className="search-area">
        <SearchFilter clearFilters={clearFilters} handleFilterChange={handleFilterChange} filters={filterOptions} filterText={filterText} expanded={filterOptions && Object.keys(filterOptions).length > 0} />
        <SearchBar placeholder="Search the Technical Information Center..." handleKeyUp={handleKeyUp} filterText={filterText} onFilterTextChange={setFilterText} onSearchClick={handleSearchClick}  />
      </div>
     
      <div className="search-results" >
        { loading 
          ?
            <h3 className="center">Loading Results</h3>
          :
            <h3 className="center">({results}) Results</h3>
        }
       {/* , height: 800 */}
        <div className="center" style={{ paddingBottom: 60, width: '100%' }}   >      
          <div className="center">
          { excelLoading && <CircularProgress />}

            <Button 
              id="excel"
              variant="contained" 
              className="green"         
              
              startIcon={<FontAwesomeIcon icon={["fas", "file-export"]} />}
              onClick={exportToExcelt}>
                Export To Excel
            </Button>
          </div>

          <DataGrid //getRowHeight={() => 'auto'} 
            autoHeight
            rowHeight={100}
            sx={{ '.MuiDataGrid-columnHeaderTitle': {fontWeight: 'bolder'},
            '.MuiDataGrid-row': {cursor: 'pointer', borderBottom: 1},
            '.MuiDataGrid-columnHeaderTitleContainer': {borderBottom: 1 },
            '.MuiDataGrid-iconButtonContainer': {visibility: 'visible' },
            '.MuiDataGrid-sortIcon': {opacity: 'inherit !important' },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: '#D4AF37', // Custom color
              '&:hover': {
                backgroundColor: '#D4AF37 !important', // Hover effect
              },
            },
           }}
          rows={rows.map((result, index) => (
              {
                id: result.AssetID, 
                guid: result.AssetID, 
                ticNumber: formatTicNum(result),
                image: '',
                Title: result.Title, 
                ticParkName: result.ticParkName,
                ticParentDocId: result.ticParentDocId,
                ticParkAlpha: result.ticParkAlpha,
                ticInfoType: result.ticInfoType,
                ticDocType: result.ticDocType,
                ticPublishDate: result.ticPublishDate ? formatDate(result.ticPublishDate) : "No Date"
              }
            )) || []}
            columns={columns}
            // initialState={{
            //   pagination: {
            //     paginationModel: { page: paginationModel.page, pageSize: paginationModel.pageSize },
            //   },
            // }}
            // pagination
            paginationModel={paginationModel}
            // onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            // checkboxSelection
            isRowSelectable={(params) => isBtnDisabled(params.row)}
            //onRowSelectionModelChange={handleSelectionChange}
            apiRef={apiRef}
            loading={loading}
            onRowClick={handleRowClick}
            rowCount={results}
            disableRowSelectionOnClick
            pagination
            sortingMode="server"
            filterMode="server"
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            onSortModelChange={setSortModel}
            onFilterModelChange={setFilterModel}
          />
        </div>
      </div>
    </>
  );
}

export default SearchResult;